import { useSession } from "@internal/auth";
import { useEffect, useState } from "react";
import { getDbForUser, type PlannerDoc } from "@/lib/db";
import {
  DIARY_MEALS_TYPE,
  type DiaryMeal,
  type DiaryMealsData,
  diaryMealsId,
} from "@/lib/diary";

function docToMeals(doc: PlannerDoc): DiaryMeal[] {
  const data = (doc.data ?? {}) as Partial<DiaryMealsData>;
  return Array.isArray(data.meals) ? data.meals : [];
}

/** The meals stored for `date`, in stored order. */
export function useDiaryMeals(date: string | null): {
  meals: DiaryMeal[];
  loading: boolean;
} {
  const { user } = useSession();
  const [state, setState] = useState<{
    meals: DiaryMeal[];
    loading: boolean;
  }>({ meals: [], loading: true });

  useEffect(() => {
    if (!user || !date) {
      setState({ meals: [], loading: false });
      return;
    }
    let cancelled = false;
    let sub: { unsubscribe: () => void } | null = null;
    (async () => {
      const db = await getDbForUser(user.id);
      if (cancelled) return;
      sub = db.daily_planner
        .findOne(diaryMealsId(user.id, date))
        .$.subscribe((doc) => {
          setState({
            meals: doc ? docToMeals(doc.toJSON() as PlannerDoc) : [],
            loading: false,
          });
        });
    })();
    return () => {
      cancelled = true;
      sub?.unsubscribe();
    };
  }, [user, date]);

  return state;
}

/**
 * Every stored date with startDate ≤ date ≤ endDate and its meals, sorted
 * ascending by date. Queries by the indexed (user_id, type) pair and filters
 * the range in memory — the per-user collection is small.
 */
export function useDiaryMealsRange(
  startDate: string | null,
  endDate: string | null,
): { days: DiaryMealsData[]; loading: boolean } {
  const { user } = useSession();
  const [state, setState] = useState<{
    days: DiaryMealsData[];
    loading: boolean;
  }>({ days: [], loading: true });

  useEffect(() => {
    if (!user || !startDate || !endDate) {
      setState({ days: [], loading: false });
      return;
    }
    let cancelled = false;
    let sub: { unsubscribe: () => void } | null = null;
    (async () => {
      const db = await getDbForUser(user.id);
      if (cancelled) return;
      sub = db.daily_planner
        .find({ selector: { user_id: user.id, type: DIARY_MEALS_TYPE } })
        .$.subscribe((docs) => {
          const days = docs
            .map((d) => {
              const doc = d.toJSON() as PlannerDoc;
              const data = (doc.data ?? {}) as Partial<DiaryMealsData>;
              return {
                date: typeof data.date === "string" ? data.date : "",
                meals: docToMeals(doc),
              };
            })
            .filter((day) => day.date >= startDate && day.date <= endDate)
            .sort((a, b) => (a.date < b.date ? -1 : 1));
          setState({ days, loading: false });
        });
    })();
    return () => {
      cancelled = true;
      sub?.unsubscribe();
    };
  }, [user, startDate, endDate]);

  return state;
}
